import { observer }               from 'mobx-react-lite';
import { Shape }                  from '@tokamap/core';
import { PlusOutlined }           from '@ant-design/icons';
import { FormatPainterOutlined }  from '@ant-design/icons';
import { StarOutlined }           from '@ant-design/icons';
import ListCollection             from '@widesk-ui/components/listCollection/ListCollection';
import Button                     from '@widesk-ui/components/Button';
import ListEmptyState             from '@/TokamapEditor/ux/ListEmptyState';
import { message }                from '@widesk-ui/hooks/useMessage';
import Tooltip                    from '@widesk-ui/components/Tooltip';
import useDesignTokens            from '@/hooks/useDesignTokens';
import View                       from '@widesk-ui/components/View';
import ThemeModel                 from '@models/map/ThemeModel';
import leftSidebarStore           from '@/TokamapEditor/stores/leftSidebarStore';
import themeStore                 from '@/TokamapEditor/stores/themeStore';
import currentProjectEditorStore  from '@/TokamapEditor/stores/currentProjectEditorStore';
import ZoneCountButton            from '@/TokamapEditor/ux/ZoneCountButton';
import ThemeSwatch                from '@/TokamapEditor/ux/ThemeSwatch';
import ShapeHoverRow              from '@/TokamapEditor/ux/ShapeHoverRow';
import { ShapeHoverFilter }        from '@/TokamapEditor/ux/ShapeHoverRow';
import CompactControls from '@/TokamapEditor/ux/CompactControls';

const shapeFilterOfTheme = (m: ThemeModel) => {
	const theme = themeStore.getTheme(String(m.id));

	return theme && ((shape: Shape) => shape.themeId === theme.id);
};

/**
 * Chaque ligne se rend depuis `themeStore` et non depuis le modèle de la collection : le store porte
 * les écritures, lui seul a la version à jour. Le modèle n'est qu'un instantané du chargement.
 */
const ThemeListSidebar = observer(() => {
	const tokens = useDesignTokens();
	const project = currentProjectEditorStore.get.project;

	// Même structure que `PlaceTypeListSidebar` : le conteneur remplit le tiroir pour que le bouton
	// tombe en bas quand la liste est courte, et reste collé quand elle déborde.
	return (
		<CompactControls>
			<View style={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
				<View flex>
					<ShapeHoverFilter.Provider value={shapeFilterOfTheme}>
						<ListCollection
							modelClass={ThemeModel}
							RowComponent={ShapeHoverRow}
							defaultSortName="label"
							// Indispensable à l'affichage du bandeau d'outils, où loge la recherche : `ListCollection`
							// ne le rend que si `collectionSorts`, `collectionFilters` ou `massActions` est fourni.
							// Tris bornés à ce que l'API accepte.
							collectionSorts={{
								label: {
									labels: ['De A à Z', 'De Z à A'],
									value: 'label',
								},
								createdAt: {
									labels: ['Du plus récent au plus ancien', 'Du plus ancien au plus récent'],
									value: 'createdAt',
								},
							}}
							// La ligne entière ouvre le thème : c'était la raison principale de cliquer dessus, et
							// la garder derrière le menu `…` la mettait à deux clics.
							clickable
							onClickItem={m => {
								const theme = themeStore.getTheme(String((m as ThemeModel).id));

								if (theme) leftSidebarStore.openThemeForm(theme);
							}}
							onDidMount={listStore => {
								// Enregistrement inconditionnel : le garde `if (!getListStore(...))` du patron voisin
								// laissait le registre pointer une instance démontée après une réouverture de la
								// sidebar, et tout `reload()` extérieur retombait dans le vide.
								leftSidebarStore.setListStore('themes', listStore);
							}}
							onFetchCollectionStart={async coll => {
								coll.setRequiredFilter('project', project.id);
							}}
							// Le store porte le rendu des lignes : rien ne s'affiche tant qu'il n'a pas la donnée.
							onFetchCollectionEnd={async () => {
								await themeStore.load();
							}}
							emptyText={<ThemesEmptyState />}
							itemProps={m => {
								const theme = themeStore.getTheme(String(m.id));

								if (!theme) return { title: m.label };

								return {
									// `renderLeft` convient ici, contrairement à l'étoile : la pastille est présente sur
									// TOUTES les lignes, donc les noms restent alignés entre elles.
									renderLeft: () => <ThemeSwatch theme={theme} />,
									// Même affordance que dans les listes de points d'intérêts et de types : le compte est
									// annoncé, le clic met en évidence, et le bouton s'éteint quand il n'y a rien à montrer.
									renderRight: () => (
										<ZoneCountButton
											filter={shape => shape.themeId === theme.id}
											suffix={count => `liée${count > 1 ? 's' : ''}`}
										/>
									),
									// L'étoile, elle, est accolée au nom : en `renderRight` elle atterrissait dans le même
									// conteneur que le menu `…` (voir `ListItem`), collée à lui.
									title: (
										<View row gap="xs" centerV>
											{theme.name}

											{theme.default && <DefaultThemeMarker />}
										</View>
									),
									actions: [
										{
											key: 'apply',
											icon: <FormatPainterOutlined />,
											label: 'Appliquer à des formes',
											onClick: () => leftSidebarStore.openThemeApply(theme),
										},
										{
											key: 'default',
											icon: <StarOutlined />,
											// Bascule : un projet peut légitimement n'avoir AUCUN thème par défaut.
											label: theme.default ? 'Retirer le thème par défaut' : 'Définir comme thème par défaut',
											onClick: async () => {
												try {
													await themeStore.setDefaultTheme(theme.default ? undefined : theme.id);

													message.success(theme.default
														? `Le projet n'a plus de thème par défaut.`
														: `"${theme.name}" est désormais le thème par défaut.`);
												} catch (err) {
													message.exception(err);
												}
											},
										},
									],
									extraProps: {
										paddingH: 'md',
										paddingV: 'sm',
									},
								};
							}}
						/>
					</ShapeHoverFilter.Provider>
				</View>

				{/* Même bouton qu'en bas de la liste des types : l'action principale d'une liste est de
				    l'allonger, et elle se trouve donc à sa fin. Il reste en place quand la liste est vide —
				    l'état vide n'a donc pas le sien, qui ferait deux fois la même proposition à deux lignes
				    d'écart. */}
				<View
					p="sm"
					bg={tokens.surface.card}
					style={{ position: 'sticky', bottom: 0, borderTop: `1px solid ${tokens.border.subtle}` }}
				>
					<Button
						block
						size="large"
						type="primary"
						icon={<PlusOutlined />}
						onClick={() => leftSidebarStore.addAndEditTheme()}
					>
						Ajouter un thème
					</Button>
				</View>
			</View>
		</CompactControls>
	);
});

/**
 * Marque le thème appliqué d'office aux nouvelles formes. Même icône que l'action qui le désigne,
 * pour qu'on relie les deux d'un coup d'œil ; l'infobulle porte le libellé que l'icône seule perd.
 */
const DefaultThemeMarker = () => {
	const tokens = useDesignTokens();

	return (
		<Tooltip title="Thème par défaut, appliqué aux nouvelles formes">
			<StarOutlined style={{ color: tokens.color.primary, fontSize: 16 }} />
		</Tooltip>
	);
};

const ThemesEmptyState = () => (
	<ListEmptyState
		title="Aucun thème"
		hint="Un thème est un style — couleurs, bordure — que l'on applique d'un clic à plusieurs zones de la carte."
	/>
);

export default ThemeListSidebar;
